import { Popover, Transition } from "@headlessui/react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Calendar from "../Calendar";
import Input from "../Input";

export const DatePicker = (props) => {
  const buttonRef = useRef(null);
  const calendarRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const calendarWidth = 350; // Calendar's natural width from CSS
      const viewportWidth = window.innerWidth;

      // Calculate left position, ensuring calendar doesn't go off-screen
      let leftPosition = rect.left + window.scrollX;

      // If calendar would extend beyond viewport, adjust position
      if (leftPosition + calendarWidth > viewportWidth) {
        leftPosition = rect.right + window.scrollX - calendarWidth;
      }

      // Ensure minimum left position (don't go off left edge)
      leftPosition = Math.max(8, leftPosition); // 8px minimum margin

      setPosition({
        top: rect.bottom + window.scrollY,
        left: leftPosition,
      });
    }
  };

  useEffect(() => {
    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  function handleChange(e) {
    const finalValue = formatDate(e);
    const dbValue = e;
    const data = {
      value: dbValue,
    };

    if (props.id) {
      data.id = props.id;
    }

    props.onChange && props.onChange(data);
  }

  function formatDate(value, dbFormat) {
    const newDate = new Date(value);
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    const date = newDate.getDate();

    if (dbFormat) {
      return `${month}-${date}-${year}`;
    }

    return `${month}-${date}-${year}`;
  }

  const trigger = (
    <div className="relative w-full">
      <Input
        placeholder={props.placeholder}
        required={props.required}
        value={props.value}
        label={props.label}
        size={props.size}
        onBlur={props.onBlur}
        icon={{
          right: <CalendarDaysIcon className="w-5 h-5" />,
        }}
      />
    </div>
  );

  const calendar = <Calendar value={props.value} onChange={handleChange} />;

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            ref={buttonRef}
            className="w-full"
            onClick={updatePosition}
          >
            {trigger}
          </Popover.Button>
          {open &&
            createPortal(
              <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Popover.Panel
                  ref={calendarRef}
                  static
                  className="fixed z-[1200] bg-white shadow-lg rounded-lg border border-gray-200"
                  style={{
                    top: `${position.top + 4}px`,
                    left: `${position.left}px`,
                  }}
                >
                  {calendar}
                </Popover.Panel>
              </Transition>,
              document.body
            )}
        </>
      )}
    </Popover>
  );
};

export default DatePicker;
